export const TOPPAY_ORDER_POLL_MS = 3_000;
export const TOPPAY_ORDER_POLL_MAX_MS = 90_000;

export function normalizeTopPayOrdersPage(data) {
  const rows = Array.isArray(data?.rows)
    ? data.rows
    : Array.isArray(data?.data?.rows)
    ? data.data.rows
    : Array.isArray(data)
    ? data
    : [];
  const total = Number(data?.total ?? data?.data?.total ?? rows.length) || 0;
  return { rows, total };
}

export function normalizeTopPayOrderStatus(status) {
  return String(status ?? '')
    .trim()
    .toUpperCase();
}

export function extractTopPayOrderId(order) {
  return String(order?.merchantOrderNo ?? order?.orderId ?? order?.id ?? '').trim();
}

export function findTopPayOrderById(orders, orderId) {
  const list = Array.isArray(orders) ? orders : [];
  const target = String(orderId ?? '').trim();
  if (!target || !list.length) return null;
  return (
    list.find((row) => String(extractTopPayOrderId(row)).toLowerCase() === target.toLowerCase()) ||
    null
  );
}

export function isTopPayReturnTerminalPhase(phase) {
  const p = String(phase ?? '').trim();
  return p === 'success' || p === 'failed' || p === 'neutral';
}

export function resolveTopPayReturnPhase(
  status,
  { timedOut = false, ledgerCredited = false } = {}
) {
  const s = normalizeTopPayOrderStatus(status);
  if (s === 'SUCCESS' && ledgerCredited) return 'success';
  if (s === 'SUCCESS') return 'pay_success';
  if (s === 'FAILED' || s === 'EXPIRED' || s === 'PAY_FAIL') return 'failed';
  if (timedOut) return 'neutral';
  if (s === 'PENDING_VERIFY') return 'pending';
  return 'pending';
}

/** Account「法币」充值日志可见态（隐藏待支付 PENDING/CREATED） */
export const TOPPAY_FIAT_RECHARGE_VISIBLE_STATUSES = Object.freeze(
  new Set(['SUCCESS', 'FAILED', 'EXPIRED', 'PENDING_VERIFY'])
);

export function isVisibleTopPayFiatRechargeStatus(status) {
  return TOPPAY_FIAT_RECHARGE_VISIBLE_STATUSES.has(normalizeTopPayOrderStatus(status));
}

function readTopPayDecimal(value) {
  if (value == null) return NaN;
  if (typeof value === 'object' && 's' in value) return Number(value.s);
  return Number(value);
}

/**
 * Normalize TopPay list VO → account fiat-table row (Alchemy-compatible shape).
 * Amount shows channel fiat paid (VND integer / BRL·MXN 2dp); currency column is USD credit asset.
 */
export function mapTopPayToFiatRechargeRow(row, labels = {}) {
  const status = normalizeTopPayOrderStatus(row?.status);
  const credited = row?.ledgerCredited === true;
  const merchant = String(row?.merchantOrderNo || '').trim();
  const platformId = row?.id != null && String(row.id).trim() !== '' ? String(row.id) : '';
  const method = String(row?.method || '').trim();
  const createdAt = String(row?.createdAt || '').trim();
  const payFiat = readTopPayDecimal(row?.payMoneyFiat ?? row?.payMoneyVnd);
  const creditUsd = readTopPayDecimal(row?.creditUsd);
  const fiatCurrency = String(
    row?.fiatCurrency ||
      (row?.region === 'BR'
        ? 'BRL'
        : row?.region === 'MX'
        ? 'MXN'
        : row?.region === 'IN'
        ? 'INR'
        : row?.region === 'US'
        ? 'USD'
        : 'VND')
  ).toUpperCase();

  let fiatStatus = status;
  if (status === 'SUCCESS' && credited) fiatStatus = 'FINISHED';
  else if (status === 'SUCCESS') fiatStatus = 'PAY_SUCCESS';
  else if (status === 'FAILED') fiatStatus = 'PAY_FAIL';
  else if (status === 'PENDING_VERIFY') fiatStatus = 'PAY_SUCCESS';
  else if (status === 'EXPIRED') fiatStatus = 'EXPIRED';

  const apiStatusText = String(row?.statusText || '').trim();
  const statusText =
    labels.statusText ||
    apiStatusText ||
    (status === 'SUCCESS' && !credited ? labels.creditingText : '') ||
    fiatStatus ||
    status ||
    '—';

  let statusTone = 'unknown';
  if (fiatStatus === 'FINISHED' || (status === 'SUCCESS' && credited)) statusTone = 'success';
  else if (fiatStatus === 'PAY_SUCCESS' || status === 'PENDING_VERIFY') statusTone = 'pending';
  else if (fiatStatus === 'PAY_FAIL' || fiatStatus === 'EXPIRED') statusTone = 'failed';

  const amountLabel = Number.isFinite(payFiat)
    ? fiatCurrency === 'VND'
      ? `${payFiat.toLocaleString('en-US', { maximumFractionDigits: 0 })} VND`
      : `${payFiat.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} ${fiatCurrency}`
    : Number.isFinite(creditUsd)
    ? `${creditUsd.toLocaleString('en-US', { maximumFractionDigits: 6 })} USD`
    : '—';

  const txid = String(row?.txHash || '').trim();

  return {
    id: `toppay-${platformId || merchant || createdAt}`,
    orderId: merchant || platformId || '—',
    depositAmountLabel: amountLabel,
    statusText,
    statusTone,
    failMessage: '',
    payType: labels.payType || method || 'TopPay',
    chainLabel: '—',
    currencyLabel: 'USD',
    timeLabel: createdAt || '—',
    sortAt: createdAt,
    depositTxid: txid,
    depositTxidShort: txid.length > 14 ? `${txid.slice(0, 6)}…${txid.slice(-4)}` : txid || '—',
    kind: 'toppay',
    fiatStatus,
  };
}
